/**
 * The Owners page's payouts view (COL-474), opened from the "Payouts ready"
 * stat tile.
 *
 * One row per PAYOUT — an owner × property group for one month, the same unit
 * the stat tile counts (computeOwnerPayoutGroups in src/lib/owner-payouts.ts).
 * Every payout is listed, and the Status column says where it stands — there
 * are no status chips above the table:
 *
 *   - Pending: rent is owed and not all of it is in. The row says how much and
 *     how many rooms are still missing, and what the owner would receive if
 *     the payout ran now.
 *   - Ready: every tenancy is paid; the payout is waiting to be made.
 *   - Completed: paid out — every offset invoice for the month is marked paid
 *     and all rent is in. A month paid out early stays Pending or Ready on the
 *     remainder invoice that bills the rest of its rent (COL-483). Listed for
 *     this month only: once the month turns, a completed payout leaves the
 *     table.
 *
 * The live payout is rent collected minus the collected-based fee. When the
 * month's offset invoice exists that fee IS the invoice's amount, which the DB
 * keeps current as payments arrive, so the number here and the document agree.
 *
 * Payouts left over from earlier months sit in a band ABOVE this month's
 * (COL-515): a Pending or Ready payout does not stop being owed when the month
 * turns, so it stays at the top, oldest first, until it is paid out. The band
 * headers only appear while there is something carried over — otherwise the
 * table is this month's alone, as before.
 *
 * Replaces the owner link-state chips while the payouts tile is active: those
 * describe an owner's record, not a payout.
 */
import { useMemo, useState } from "react";
import { CalendarDays, History } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { EmptyState } from "@/components/admin/v2/primitives/EmptyState";
import { useLanguage } from "@/contexts/LanguageContext";
import { useIsMobile } from "@/hooks/use-mobile";
import type { OwnerPayoutGroup } from "@/lib/owner-payouts";
import { cn } from "@/lib/utils";
import { OwnerPayoutDetailDialog } from "./OwnerPayoutDetailDialog";
import {
  apartmentCount,
  eur,
  monthName,
  StatusPill,
  type PayoutViewItem,
  type Row,
  type Translate,
} from "./owner-payouts-parts";

/** Every payout group with a status, flattened out of the owner rows. Groups
 *  with nothing to act on (status null) have no status to show and are dropped.
 *  Carried-over groups (COL-515) are listed oldest month first; this month's
 *  by owner and property. */
function payoutViewItems(rows: Row[]): PayoutViewItem[] {
  const items: PayoutViewItem[] = [];
  for (const row of rows) {
    const lessor = row.lessor;
    if (!lessor) continue;
    const add = (group: OwnerPayoutGroup, carriedOver: boolean) => {
      const { status } = group;
      if (!status) return;
      const property = lessor.propertyList?.find(
        (p) => String(p.id) === group.propertyId
      );
      items.push({
        row,
        group: { ...group, status },
        propertyTitle: property?.title ?? null,
        carriedOver,
      });
    };
    for (const group of lessor.payoutGroups ?? []) add(group, false);
    for (const group of lessor.carriedOverPayoutGroups ?? []) {
      // Only unfinished payouts carry over: a completed one is listed for its
      // own month alone, so paid-out history never piles up in the table.
      if (group.status !== "completed") add(group, true);
    }
  }
  return items.sort(
    (a, b) =>
      (a.carriedOver ? a.group.periodMonth : "").localeCompare(
        b.carriedOver ? b.group.periodMonth : ""
      ) ||
      (a.row.lessor?.name ?? "").localeCompare(b.row.lessor?.name ?? "") ||
      (a.propertyTitle ?? "").localeCompare(b.propertyTitle ?? "")
  );
}

function CollectedCell({
  group,
  t,
  month,
}: {
  group: PayoutViewItem["group"];
  t: Translate;
  /** Set for a payout carried over from an earlier month: the percentage is
   *  of THAT month's rent, so the line names it instead of "this month". */
  month?: string;
}) {
  if (group.status === "completed") {
    return (
      <span className="text-sm text-muted-foreground">
        {t("admin.owners.payouts.paidOut", "Paid out")}
      </span>
    );
  }
  const pending = group.status === "pending";
  // collectedEur, not paidEur: one tenancy's overpayment must not fill the
  // bar for another tenancy's missing rent.
  const raw =
    group.expectedEur > 0 ? (group.collectedEur / group.expectedEur) * 100 : 0;
  // Never round a pending group up to a full bar: 99.6% is still money missing.
  const pct = pending
    ? Math.min(99, Math.floor(raw))
    : Math.min(100, Math.round(raw));
  return (
    <div className="min-w-0 space-y-1">
      <p className="text-sm">
        <span className="font-medium tabular-nums">{pct}%</span>{" "}
        <span className="text-muted-foreground">
          {month
            ? t(
                "admin.owners.payouts.pctOfMonthRentCollected",
                "of {month} rent collected"
              ).replace("{month}", month)
            : t(
                "admin.owners.payouts.pctOfRentCollected",
                "of rent collected this month"
              )}
        </span>
      </p>
      <div
        className="h-1.5 overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
      >
        <div
          className={cn(
            "h-full rounded-full",
            pending ? "bg-amber-500" : "bg-emerald-500"
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
      <p
        className={cn(
          "text-xs",
          pending ? "text-amber-700" : "text-muted-foreground"
        )}
      >
        {pending && group.ledgerOutstanding && group.roomsUnpaid === 0 ? (
          // Every tenancy reads paid, but the invoice's own ledger still has
          // an open rent row (COL-483) — say that, not "0 of n tenants unpaid".
          month ? (
            t(
              "admin.owners.payouts.ledgerRowOpenMonth",
              "A rent payment for {month} is still open"
            ).replace("{month}", month)
          ) : (
            t(
              "admin.owners.payouts.ledgerRowOpen",
              "A rent payment for this month is still open"
            )
          )
        ) : pending ? (
          // Counts tenancies, not rooms (COL-625): a room let twice in the
          // month is two payout lines.
          <>
            <span className="tabular-nums">
              {group.roomsUnpaid} {t("admin.owners.payouts.of", "of")}{" "}
              {group.roomsOwing}
            </span>{" "}
            {t("admin.owners.payouts.tenantsUnpaid", "tenants unpaid")}
          </>
        ) : (
          <>
            {t("admin.owners.payouts.allRoomsPaidPrefix", "All")}{" "}
            <span className="tabular-nums">{group.roomsOwing}</span>{" "}
            {t("admin.owners.payouts.tenantsPaid", "tenants paid")}
          </>
        )}
      </p>
    </div>
  );
}

/** One label/value line of the payout breakdown — every figure in the cell is
 *  laid out the same way, label left, amount right. */
function PayoutLine({
  label,
  value,
  strong,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-baseline justify-between gap-3",
        strong ? "text-sm font-semibold" : "text-xs text-muted-foreground"
      )}
    >
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}

/** The payout as the sum it is — rent collected, less the live fee, equals the
 *  live net payout — so the bold figure never reads as the rent itself.
 *
 *  Once part of the month is paid out (an early payout left a remainder,
 *  COL-483), the sum is only what is still owed: the rent collected since that
 *  payout less the fees not yet settled, with the earlier payout on its own
 *  line so it is never paid a second time. */
function PayoutCell({ group, t }: { group: OwnerPayoutGroup; t: Translate }) {
  const partlyPaidOut = group.paidOutEur !== null;
  return (
    <div className="min-w-0 space-y-1">
      {partlyPaidOut && (
        <PayoutLine
          label={t("admin.owners.payouts.alreadyPaidOut", "Already paid out")}
          value={eur(group.paidOutEur ?? 0)}
        />
      )}
      <PayoutLine
        label={
          partlyPaidOut
            ? t(
                "admin.owners.payouts.rentCollectedSincePayout",
                "Rent collected since the last payout"
              )
            : t("admin.owners.payouts.rentCollected", "Rent collected")
        }
        value={eur(group.payoutRentEur)}
      />
      <PayoutLine
        label={t("admin.owners.payouts.liveColiviqFee", "Live coliviq Fee")}
        value={eur(group.liveFeeEur)}
      />
      <div className="border-t border-border/60 pt-1">
        <PayoutLine
          label={t("admin.owners.payouts.liveNetPayout", "Live Net Payout")}
          value={eur(group.livePayoutEur)}
          strong
        />
      </div>
      {group.invoiceFrozen && (
        <p className="text-xs text-amber-700">
          {t(
            "admin.owners.payouts.frozen",
            "Invoice sent before payout — amount frozen"
          )}
        </p>
      )}
      {group.openRemainder && (
        <p className="text-xs text-muted-foreground">
          {t(
            "admin.owners.payouts.remainderOpen",
            "Remainder {invoice} covers the rent collected after {previous}"
          )
            .replace("{invoice}", group.openRemainder.invoiceNo ?? "—")
            .replace("{previous}", group.openRemainder.followsInvoiceNo ?? "—")}
        </p>
      )}
    </div>
  );
}

function OwnerPropertyLabel({
  item,
  t,
  month,
}: {
  item: PayoutViewItem;
  t: Translate;
  /** The carried-over month's name — set only for those rows. The same owner
   *  and property can have a payout in two bands, so the chip is what tells
   *  the September remainder from the October payout. */
  month?: string;
}) {
  const apts = apartmentCount(item.group);
  return (
    <div className="min-w-0">
      <div className="truncate text-sm font-medium">
        {item.row.lessor?.name ?? "—"}
      </div>
      <div className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
        {/* Only the property text gives way when the row is narrow; the month
            chip must stay, it is what tells two bands' rows apart. */}
        <span className="min-w-0 truncate">
          {item.propertyTitle ?? t("admin.owners.payouts.property", "Property")}
          {apts > 0 && (
            <>
              {" · "}
              <span className="tabular-nums">{apts}</span>{" "}
              {apts === 1
                ? t("admin.owners.payouts.apartment", "apartment")
                : t("admin.owners.payouts.apartments", "apartments")}
            </>
          )}
        </span>
        {month && (
          <span className="inline-flex shrink-0 rounded-full border px-2 py-px text-[11px] font-medium text-muted-foreground">
            {month}
          </span>
        )}
      </div>
    </div>
  );
}

const HEAD =
  "h-10 px-4 text-xs font-medium uppercase tracking-wide text-muted-foreground";

/** What opens a band, as one line: carried-over payouts say how many there are
 *  and what they add up to; this month's say which month it is. */
function BandLabel({
  carriedOver,
  items,
  language,
  t,
}: {
  carriedOver: boolean;
  items: PayoutViewItem[];
  language: string | undefined;
  t: Translate;
}) {
  const count = items.length;
  const word =
    count === 1
      ? t("admin.owners.payouts.payoutOne", "payout")
      : t("admin.owners.payouts.payoutMany", "payouts");
  if (carriedOver) {
    const totalEur =
      Math.round(
        items.reduce((sum, i) => sum + i.group.livePayoutEur, 0) * 100
      ) / 100;
    return (
      <span
        role="heading"
        aria-level={3}
        className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs"
      >
        <History className="h-3.5 w-3.5 text-amber-600" aria-hidden="true" />
        <span className="font-medium text-foreground">
          {t(
            "admin.owners.payouts.carriedOver",
            "Carried over from earlier months"
          )}
        </span>
        <span className="tabular-nums text-muted-foreground">
          · {count} {word} · {eur(totalEur)}{" "}
          {t("admin.owners.payouts.liveNetTotal", "live net payout")}
        </span>
      </span>
    );
  }
  return (
    <span
      role="heading"
      aria-level={3}
      className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs"
    >
      <CalendarDays
        className="h-3.5 w-3.5 text-muted-foreground"
        aria-hidden="true"
      />
      <span className="font-medium text-foreground">
        {monthName(items[0].group.periodMonth, language, true)}
      </span>
      <span className="tabular-nums text-muted-foreground">
        · {t("admin.owners.payouts.thisMonth", "this month")} · {count} {word}
      </span>
    </span>
  );
}

export function OwnerPayoutsView({
  rows,
  onOpenOwner,
}: {
  rows: Row[];
  /** Opens the full owner profile — from the payout popup's own button. */
  onOpenOwner: (row: Row) => void;
}) {
  const { t, language } = useLanguage();
  const isMobile = useIsMobile();
  // The payout whose popup is open (COL-617): a row opens its rooms, not the
  // owner profile.
  const [selected, setSelected] = useState<PayoutViewItem | null>(null);

  const items = useMemo(() => payoutViewItems(rows), [rows]);
  const carriedOver = items.filter((i) => i.carriedOver);
  const current = items.filter((i) => !i.carriedOver);
  // Band headers only while something is carried over: without it the table is
  // this month's alone and a lone header would say nothing.
  const showBands = carriedOver.length > 0;

  const itemKey = (item: PayoutViewItem) =>
    `${item.row.key}:${item.group.propertyId}:${item.group.periodMonth}`;
  // A carried-over payout's month, named — undefined for this month's.
  const monthOf = (item: PayoutViewItem) =>
    item.carriedOver ? monthName(item.group.periodMonth, language) : undefined;
  // The open popup follows the list: after a write from inside it (COL-621,
  // marking a kickback) the owners list refetches and the popup shows the
  // recomputed payout, not the snapshot it was opened with. A payout that left
  // the list keeps its last snapshot until the popup closes.
  const selectedLive = selected
    ? (items.find((i) => itemKey(i) === itemKey(selected)) ?? selected)
    : null;

  const renderRow = (item: PayoutViewItem) => (
    <TableRow
      key={itemKey(item)}
      onClick={() => setSelected(item)}
      className="group cursor-pointer border-b border-border/60 align-top transition-colors hover:bg-muted/40"
    >
      <TableCell className="relative p-4">
        <span className="pointer-events-none absolute inset-y-0 left-0 w-[3px] bg-primary opacity-0 transition-opacity group-hover:opacity-100" />
        <OwnerPropertyLabel item={item} t={t} month={monthOf(item)} />
      </TableCell>
      <TableCell className="p-4">
        <StatusPill status={item.group.status} t={t} />
      </TableCell>
      <TableCell className="p-4">
        <CollectedCell group={item.group} t={t} month={monthOf(item)} />
      </TableCell>
      <TableCell className="p-4">
        <PayoutCell group={item.group} t={t} />
      </TableCell>
    </TableRow>
  );

  const renderCard = (item: PayoutViewItem) => (
    <li
      key={itemKey(item)}
      role="button"
      tabIndex={0}
      onClick={() => setSelected(item)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setSelected(item);
        }
      }}
      className="flex cursor-pointer flex-col gap-3 p-4 transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
    >
      <div className="flex items-start justify-between gap-2">
        <OwnerPropertyLabel item={item} t={t} month={monthOf(item)} />
        <StatusPill status={item.group.status} t={t} />
      </div>
      <CollectedCell group={item.group} t={t} month={monthOf(item)} />
      <PayoutCell group={item.group} t={t} />
    </li>
  );

  const bandRow = (key: string, band: PayoutViewItem[], carried: boolean) => (
    <TableRow
      key={key}
      className="border-b border-border/60 bg-muted/40 hover:bg-muted/40"
    >
      <TableCell colSpan={4} className="px-4 py-2">
        <BandLabel
          carriedOver={carried}
          items={band}
          language={language}
          t={t}
        />
      </TableCell>
    </TableRow>
  );

  const bandCard = (key: string, band: PayoutViewItem[], carried: boolean) => (
    <li key={key} className="bg-muted/40 px-4 py-2">
      <BandLabel carriedOver={carried} items={band} language={language} t={t} />
    </li>
  );

  if (items.length === 0) {
    return (
      <Card className="border-dashed">
        <EmptyState
          title={t(
            "admin.owners.payouts.emptyTitle",
            "No payouts in this view"
          )}
          compact
        />
      </Card>
    );
  }

  const detail = (
    <OwnerPayoutDetailDialog
      item={selectedLive}
      onClose={() => setSelected(null)}
      onOpenOwner={onOpenOwner}
    />
  );

  if (isMobile) {
    // Phones get stacked cards, like the owners list (OwnersTable), not a
    // table that has to scroll sideways.
    return (
      <>
        <ul className="divide-y divide-border/60 overflow-hidden rounded-2xl border bg-card shadow-sm">
          {showBands && bandCard("band:carried", carriedOver, true)}
          {carriedOver.map(renderCard)}
          {showBands &&
            current.length > 0 &&
            bandCard("band:current", current, false)}
          {current.map(renderCard)}
        </ul>
        {detail}
      </>
    );
  }

  return (
    <>
      <div className="overflow-x-auto rounded-2xl border bg-card shadow-sm">
        <Table className="min-w-[760px] table-fixed">
          <TableHeader className="[&_tr]:border-b [&_tr]:bg-muted/40 [&_tr]:hover:bg-muted/40">
            <TableRow>
              <TableHead className={cn(HEAD, "w-[30%]")}>
                {t(
                  "admin.owners.payouts.col.ownerProperty",
                  "Owner · property"
                )}
              </TableHead>
              <TableHead className={cn(HEAD, "w-[13%]")}>
                {t("admin.owners.payouts.col.status", "Status")}
              </TableHead>
              <TableHead className={cn(HEAD, "w-[31%]")}>
                {t("admin.owners.payouts.col.collected", "Rent collected")}
              </TableHead>
              <TableHead className={cn(HEAD, "w-[26%] text-right")}>
                {t("admin.owners.payouts.col.livePayout", "Payout")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {showBands && bandRow("band:carried", carriedOver, true)}
            {carriedOver.map(renderRow)}
            {showBands &&
              current.length > 0 &&
              bandRow("band:current", current, false)}
            {current.map(renderRow)}
          </TableBody>
        </Table>
      </div>
      {detail}
    </>
  );
}
